import AddPracticeRecordRequest from "./models/practiceRecords/AddPracticeRecordRequest";
import {createAddPracticeRecordResponseFromJson} from "./factories/practiceRecordsFactory";
import AddPracticeRecordResponse from "./models/practiceRecords/AddPracticeRecordResponse";
import SignInInvalidPasswordError from "./errors/SignInInvalidPasswordError";
import SignInInvalidUsernameError from "./errors/SignInInvalidUsernameError";
import MyLibraryResponse from "./models/myLibrary/MyLibraryResponse";
import {createMyLibraryResponseFromJson} from "./factories/myLibraryFactory";
import {createLessonWithSingleModeSegmentsResponseFromJson} from "./factories/lessonWithSingleModeSegmentsFactory";
import {createMyCourseResponseFromJson} from "./factories/myCourseFactory";
import {createAuthenticationResponseFromJson} from "./factories/authenticationFactory";
import {createMyProfileResponseFromJSON} from "./factories/myProfileFactory";
import createCreateAccountResponseFromJSON from "./factories/createAccountFactory";
import ICreateAccountResponse from "./models/createAccount/ICreateAccountResponse";
import createErrorsResponseFromJSON from "./factories/errorsFactory";
import TooManyFailedLoginAttempts from "./errors/TooManyFailedLoginAttempts";
import Server500Error from "./errors/Server500Error";
import ComputerLimitExceeded from "./errors/ComputerLimitExceeded";
import {getComputerId} from "../services/device/deviceInfo";
import {appEnvVersion} from "../config/environment";
import {createMyDashboardResponseFromJSON} from "./factories/myDashboardFactory";
import {createMySongsLibraryResponseFromJSON} from "./factories/mySongsLibraryFactory";
import {createSongDetailsResponseFromJSON} from "./factories/songDetailsFactory";
import {userLibraryAddLessonResponse} from "./factories/user-library/userLibraryAddLessonResponseFactory";
import {logger} from "../services/log/logger";
import {requestMaxRetryDefault} from "../config/network";
import {
  UnlockedSongLessonsResponse,
  UnlockedSongLessonsSchema
} from "./models/unlockedSongLessons/UnlockedSongLessonsResponse";
import {MusicHomeResponse, MusicHomeSchema} from "./models/musicHome/MusicHomeResponse";
import {baseUrlForApi} from "./PlaygroundSettings";
import {PlaylistResponse, PlaylistSchema} from "./models/playlist/PlaylistResponse";
import {
  ArrangementAccessCheckResponse,
  ArrangementAccessCheckSchema
} from "./models/arrangementAccessCheck/ArrangementAccessCheckResponse";
import { createMyCoursesResponseFromJson } from "./factories/myCoursesFactory";
import MyCoursesResponse from "./models/myCourses/MyCoursesResponse";
import ScoringMechanism from "../models/view/lessonPage/ScoringMechanism";
import LessonPermissionsError from "./errors/LessonPermissionsError";
import { deviceStore } from "../redux/device/deviceStore";

// device info to include in all endpoint requests
const getDeviceInfoHeaders = () => ({
  "x-playground-computer-id": getComputerId(),
  "x-playground-app-version": appEnvVersion,
  "x-playground-app-os-name": deviceStore.getDeviceOs() ?? "unknown",
  "x-playground-app-os-version": deviceStore.getDeviceOsVersion() ?? "unknown",
  "x-playground-app-type": deviceStore.getDeviceFormFactor(),
});

const getCommonHeaders = () => new Headers({
  // include device info headers first, so they don't overwrite anything more
  ...getDeviceInfoHeaders(),
  "Content-Type": "application/x-www-form-urlencoded",
  "Accept": "*/*",
  "accept-encoding": "gzip, deflate",
});

interface IApiResponse {
  error?: {
    type: string;
    code: number;
    message: string;
  };
}

/**
 * Used to make calls to the backend service to retrieve all data needed in the app.
 */
export default class PlaygroundAPI {
  public readonly baseUrl: string;
  sessionId?: string;
  sessionToken?: string;
  userId?: number;
  constructor({ baseUrl = baseUrlForApi, sessionId, sessionToken, userId }: {baseUrl?: string; sessionId?: string; sessionToken?: string; userId?: number} = {baseUrl: baseUrlForApi}) {
    Object.assign(this, { baseUrl, sessionId, sessionToken, userId, });
  }

  deleteUserSession(){
    this.sessionId = undefined;
    this.sessionToken = undefined;
    this.userId = undefined;
  }

  async authentication(username: string, password: string, computerId: string, computerType: string,){
    const url = `${this.baseUrl}/v4/authentication`;
    const {jsonResult: json, fetchResult} = await f({url, shouldConvertJsonToApiFormat:false, json: {login: username, password, computerUuid: computerId, computerType}, skipResponseErrorCheck: true});
    ensureNoAuthenticationError(json);
    await ensureNoErrorsInServerResponse(fetchResult, json);
    const authenticationResponse = createAuthenticationResponseFromJson(json);
    const {user: {id: userId,}, session: {token, id}} = authenticationResponse;
    this.sessionId = id.toString();
    this.sessionToken = token;
    this.userId = userId;
    return authenticationResponse;
  }

  async createAccount({
    username,
    email,
    password,
    computerId,
    computerType
  }: {username: string, email: string, password: string, computerId: string, computerType: string}){
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    const url = `${this.baseUrl}/v4/create-account`;
    let fetchResult;
    let json;
    try{
      ({jsonResult: json, fetchResult} = await f({
        url,
        shouldConvertJsonToApiFormat:false,
        json: {username, email, password, computerUuid: computerId, computerType, screenWidth, screenHeight},
        skipResponseErrorCheck: true
      }));
    }catch(e){
      logger.api.error({message: `fetch exception`, error: e as Error});
      throw e;
    }

    if(fetchResult?.status !== 200){
      logger.api.error({message: `fetchResult status not 200` + fetchResult});
      const errorsResponse = createErrorsResponseFromJSON(json);
      throw errorsResponse;
    }

    const createAccountResponse = createCreateAccountResponseFromJSON(json);
    const {user: {id: userId,}, session: {token, id}} = createAccountResponse as ICreateAccountResponse;
    this.sessionId = id.toString();
    this.sessionToken = token;
    this.userId = userId;

    return createAccountResponse;
  }

  async myLibrary({sessionId=this.sessionId, sessionToken=this.sessionToken, }: {instrument?: string; sessionId?: string; sessionToken?: string; } = {}): Promise<MyLibraryResponse>{
    const url = `${this.baseUrl}/v4/my-library`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    const result = createMyLibraryResponseFromJson(json);
    return result;
  }

  async myCourses({sessionId=this.sessionId, sessionToken=this.sessionToken, }: {sessionId?: string; sessionToken?: string;} = {}): Promise<MyCoursesResponse> {
    const url = `${this.baseUrl}/v4/my-courses`;
    const {jsonResult: json} = await f({url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    const result = createMyCoursesResponseFromJson(json);
    return result;
  }

  async myCourse({sessionId=this.sessionId, sessionToken=this.sessionToken, courseId, }: {sessionId?: string; sessionToken?: string; courseId: number;}){
    const url = `${this.baseUrl}/v4/my-course/${courseId}`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    return createMyCourseResponseFromJson(json);
  }

  async myProfile({sessionId=this.sessionId, sessionToken=this.sessionToken}: {sessionId?: string; sessionToken?: string;} = {}){
    const url = `${this.baseUrl}/v4/my-profile`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    return createMyProfileResponseFromJSON(json);
  }

  async myDashboard({sessionId=this.sessionId, sessionToken=this.sessionToken}: {sessionId?: string; sessionToken?: string;} = {}){
    const url = `${this.baseUrl}/v4/my-dashboard`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    return createMyDashboardResponseFromJSON(json);
  }

  async mySongsPageLibrary({sessionId=this.sessionId, sessionToken=this.sessionToken, }: {sessionId?: string; sessionToken?: string;} = {}){
    const url = `${this.baseUrl}/v4/my-song-library`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    return createMySongsLibraryResponseFromJSON(json);
  }

  /**
   * get content for song details page
   * @param songId - song id to load details for
   * @param sessionToken - user session token
   * @param practiceRecord - practice record to be added, which includes info about the lesson & segment id the practice is for, the number of missed notes, star score, hands used, etc.
   */
  async songDetails({songId, sessionId=this.sessionId, sessionToken=this.sessionToken, }: {songId: number; sessionId?: string; sessionToken?: string;}){
    const url = `${this.baseUrl}/v4/songs/${songId}`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET", userId: this.userId});
    return createSongDetailsResponseFromJSON(json);
  }

  async userLibraryAddLesson({sessionId=this.sessionId, sessionToken=this.sessionToken, lessonId, isFavorited=false}: {sessionId?: string; sessionToken?: string; lessonId: number; isFavorited?: boolean;}){
    const url = `${this.baseUrl}/v4/my-songs-v2`;

    const requestBodyJson = {
      lessonId,
      isFavorited,
    };

    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "POST", userId: this.userId, json: requestBodyJson});
    return userLibraryAddLessonResponse(json);
  }

  async userLibraryRemoveLesson({sessionId=this.sessionId, sessionToken=this.sessionToken, lessonId}: {sessionId?: string; sessionToken?: string; lessonId: number;}): Promise<boolean> {
    const url = `${this.baseUrl}/v4/my-songs-v2/${lessonId}`;
    void await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "DELETE", userId: this.userId});
    return true;
  }

  /**
   * Adds a practice record for a given lesson segment id.
   * @param sessionId - user session id
   * @param sessionToken - user session token
   * @param practiceRecord - practice record to be added, which includes info about the lesson & segment id the practice is for, the number of missed notes, star score, hands used, etc.
   * @param scoringMechanism - scoring mechanism used when the practice record was scored (MIDI or PITCH_DETECTION).
   */
  async addPracticeRecord(
    {
      sessionId = this.sessionId,
      sessionToken = this.sessionToken,
      practiceRecord
    }: {sessionId?: string; sessionToken?: string; practiceRecord: AddPracticeRecordRequest },
    scoringMechanism: ScoringMechanism
  ): Promise<AddPracticeRecordResponse>{
    const url = `${this.baseUrl}/practiceRecords/add-records`;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { lessonId: lesson_id, segmentId: segment_id, ...practiceRecordRemainingProps } = practiceRecord;
    const {jsonResult: json} = await f({
      url,
      json: {
        SessionToken: sessionToken,
        SessionId: sessionId,
        practiceRecords: [ { lesson_id, segment_id, ...practiceRecordRemainingProps } ],
        scoringMechanism: scoringMechanism,
        isCreatedByLegacyApp: false,
      }
    });
    return createAddPracticeRecordResponseFromJson(json);
  }

  async recordOnboardingScoringMechanismChoice(
    scoringMechanism: ScoringMechanism
  ): Promise<void> {
    const computerUUID = getComputerId();
    const sessionId = this.sessionId;
    const sessionToken = this.sessionToken;

    if(!sessionId || !sessionToken) {
      logger.api.error({message: `unable to record onboarding scoring mechanism choice, no session id or token`});
      return;
    }

    const url = `${this.baseUrl}/v4/onboarding-scoring-mechanism-choice`;
    await f({
      url,
      shouldConvertJsonToApiFormat: false,
      token: sessionToken,
      sessionId,
      method: "POST",
      json: {
        computerUUID: computerUUID,
        sessionToken: sessionToken,
        sessionId: sessionId,
        scoringMechanism: scoringMechanism,
      }
    });
  }

  async getLesson({sessionToken=this.sessionToken, lessonId, sessionId=this.sessionId}: {sessionToken?: string, lessonId: number, sessionId?: string}){
    const url = `${this.baseUrl}/v4/lesson-with-single-mode-segments/${lessonId}`;

    const {jsonResult: json, fetchResult} = await f({
      url,
      shouldConvertJsonToApiFormat: false,
      token: sessionToken,
      sessionId,
      method: "GET",
      userId: this.userId,
      skipResponseErrorCheck: true,
    });

    ensureNoLessonAccessRestrictions(json, fetchResult);
    await ensureNoErrorsInServerResponse(fetchResult, json);
    return createLessonWithSingleModeSegmentsResponseFromJson(json);
  }

  async courseSegmentVisit({sessionToken=this.sessionToken, sessionId=this.sessionId, segmentId, courseId}: {sessionToken?: string, segmentId: string, courseId: string, sessionId?: string}){
    const url = `${this.baseUrl}/v4/course-segment-visit`;
    const json = {courseId, segmentId};
    await f({url, json, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, userId: this.userId});
  }

  async unlockedSongLessons({sessionToken=this.sessionToken, sessionId=this.sessionId, sectionId}: {sessionToken?: string, sectionId: number, sessionId?: string}): Promise<UnlockedSongLessonsResponse> {
    const url = `${this.baseUrl}/v4/unlocked-song-lessons/${sectionId}`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET"});
    return UnlockedSongLessonsSchema.parse(json);
  }

  async musicHome({sessionToken=this.sessionToken, sessionId=this.sessionId}: {sessionToken?: string, sessionId?: string} = {}): Promise<MusicHomeResponse> {
    const url = `${this.baseUrl}/v4/music-home`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET"});
    return MusicHomeSchema.parse(json);
  }

  async playlist({sessionToken=this.sessionToken, playlistId, sessionId=this.sessionId}: {sessionToken?: string, playlistId: number, sessionId?: string}): Promise<PlaylistResponse> {
    const url = `${this.baseUrl}/v4/playlist/${playlistId}`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "GET"});
    return PlaylistSchema.parse(json);
  }

  async arrangementAccessCheck({sessionToken=this.sessionToken, arrangementId, sessionId=this.sessionId}: {sessionToken?: string, arrangementId: number, sessionId?: string}): Promise<ArrangementAccessCheckResponse> {
    const url = `${this.baseUrl}/v4/arrangement-access-check/${arrangementId}`;
    const {jsonResult: json} = await f({ url, shouldConvertJsonToApiFormat: false, token: sessionToken, sessionId, method: "POST"});
    return ArrangementAccessCheckSchema.parse(json);
  }
}

/**
 * Common function for sending/receiving data to/from the playground api.
 * @param url - url to call
 * @param method - http method to use. e.g. POST, GET, etc
 * @param headers - http headers to send
 * @param mode - cors
 * @param json - json object to send to the api. since the api doesn't speak json in POSTs, this json object is converted to a nested form encoded data format.
 * @param shouldConvertJsonToApiFormat - whether to convert json to form data (old api calls require)
 * @param token - authorization token (session token). If supplied will be added to the Authorization header.
 * @param sessionId
 * @param userId
 * @param skipResponseErrorCheck - set true for authentication endpoint only.
 */
async function f({url, method = "POST", headers = undefined, mode = "cors", json, shouldConvertJsonToApiFormat=true, token, sessionId, userId, skipResponseErrorCheck=false}: { url: string; method?: string; mode?: RequestMode; headers?: Headers; json?: object, shouldConvertJsonToApiFormat?: boolean, token?: string, sessionId?: string, userId?: number, skipResponseErrorCheck?:boolean }): Promise<{jsonResult: object, fetchResult: Response }> {
  if (headers === undefined) {
    headers = getCommonHeaders();
  }
  const body = shouldConvertJsonToApiFormat ? convertJsonToApiFormat(json) : JSON.stringify(json);
  let headersToSend = headers;
  if(token && sessionId){
    //clone so that next requests don't send the Auth header, as that will blow up old api endpoints.
    const headersAsJson = convertHeadersToJson(headersToSend);
    const h = {...headersAsJson};
    headersToSend = new Headers(h);
    headersToSend?.set("Authorization", `PlaygroundToken ${token}${sessionId}`);
  }
  if(!shouldConvertJsonToApiFormat && method === "POST" && json){
    const headersAsJson = convertHeadersToJson(headersToSend);
    const h = {...headersAsJson};
    headersToSend = new Headers(h);
    headersToSend?.set("Content-Type", `application/json`);
  }

  const fetchResult = await sendFetchWithRetries(url, { method, headers: headersToSend, mode, body,});

  let jsonResult;
  try{
    jsonResult = await fetchResult.json();
  }catch(e){
    try{
      jsonResult = await fetchResult.text();
    }catch(e2){
      logger.api.error({message: `unable to read json or text of the message`});
    }
  }
  if(!skipResponseErrorCheck){
    await ensureNoErrorsInServerResponse(fetchResult, jsonResult, userId);
  }

  return {jsonResult, fetchResult};
}

/**
 * send fetch request with retries for network errors
 *  max retry up to per number passed
 */
const sendFetchWithRetries = async (input: RequestInfo, init?: RequestInit, maxRetryRemaining: number = requestMaxRetryDefault): Promise<Response> => {
  const isAvailableToRetry = maxRetryRemaining > 0;
  try {
    return await fetch(input, init);
  } catch (e) {
    if (!isAvailableToRetry){
      // not available to retry, rethrow error
      throw e;
    }

    // see if error is one we can retry
    if (e instanceof TypeError){
      // is error related to fetch executing the request
      // @see https://developer.mozilla.org/en-US/docs/Web/API/fetch#exceptions
      // most often fetch threw a network error.
      const nextMaxRetryRemaining: number = maxRetryRemaining - 1;
      return sendFetchWithRetries(input, init, nextMaxRetryRemaining);
    }

    // no other available handling, rethrow error
    throw e;
  }
}

/**
 * Throw an exception if the status is greater than 299 or the response body contains an errors array
 * @param fetchResult
 * @param jsonResult
 * @param userId
 */
async function ensureNoErrorsInServerResponse(fetchResult: Response, jsonResult: any, userId?: number){
  if(fetchResult.status === 500){
    throw new Server500Error(`UserId: ${userId} encountered a server 500 error. ${JSON.stringify(jsonResult)}`);
  }
  if(fetchResult.status >= 300){
    throw new Error(`UserId: ${userId} encountered server error with status: ${fetchResult.status}  text: ${JSON.stringify(jsonResult)}`);
  }
  const errors = jsonResult.errors;
  if(errors && errors.length > 0){
    throw new Error(`UserId: ${userId} encountered error response from server with status: ${fetchResult.status}  text: ${JSON.stringify(jsonResult)}`);
  }
}

/**
 * Throw custom error type so we can customize error messaging on sign in page.(e.g. instead of red box, just put text above username "no user found"
 * @param response
 */
function ensureNoAuthenticationError(response: IApiResponse){
  if(!response.error){return;}
  const {error: {type: errorType, code, message}} = response;
  const parsedCode = parseInt(code !== undefined ? code.toString() : "");
  switch(errorType){
    case "AuthenticationError":
      if(parsedCode === 2001){
        throw new SignInInvalidUsernameError(message);
      }else if(parsedCode === 2002){
        throw new SignInInvalidPasswordError(message);
      }else if(parsedCode === 2003){
        throw new TooManyFailedLoginAttempts(message);
      }else if(parsedCode === 2401){
        throw new ComputerLimitExceeded(message);
      } else {
        logger.userAuth.error({message: `Unknown authentication error code: ${parsedCode} message: ${message}`});
      }
      break;
    default:
      logger.userAuth.error({message: `Unknown authentication error type: ${errorType} code: ${parsedCode} message: ${message}`});
  }
}

/**
 * Ensures that there are no lesson access restrictions by checking the API response for specific error codes and HTTP statuses.
 * 
 * This function handles the following cases:
 * - If the error type is "GenericApiError" and the error code is `5` with an HTTP status of `403`, it throws a `LessonPermissionsError`.
 * - Logs unknown error codes or types for debugging purposes.
 * 
 * @param response - The API response object containing potential error details.
 * @param fetchResult - The HTTP response object, used to check the status code.
 * @throws {LessonPermissionsError} If the error type is "GenericApiError" with code `5` and status `403`.
 */
function ensureNoLessonAccessRestrictions(response: IApiResponse, fetchResult: Response) {
  if (!response.error) {
    return;
  }
  const {error: {type: errorType, code, message}} = response;
  const parsedCode = parseInt(code !== undefined ? code.toString() : "");
  switch(errorType) {
    case "GenericApiError":
      if(parsedCode === 5 && fetchResult.status === 403) {
        throw new LessonPermissionsError(message);
      } else {
        logger.lesson.error({message: `Unknown lesson error code: ${parsedCode} message: ${message}`});
      }
      break;
    default:
      logger.lesson.error({message: `Unknown lesson error type: ${errorType} code: ${parsedCode} message: ${message}`});
  }
}

/**
 * Converts a json object into the string format our api endpoints expect (nested form encoded)
 * e.g.
 * SessionToken=470275077f06500b9cf84da64b120c2f
 * &SessionId=1463143
 * &filters%5B1%5D%5BuserId%5D=87615&computer%5Buuid%5D=90F73479-C3B3-14E0-5886-7B3F13EEC81B
 * &publishedOnly=true
 * &detail=detailedLibrary
 * &count=0
 * &offset=0&client%5Bversion%5D=2.2&sortFields%5B0%5D%5Border%5D=asc
 * ...
 * @param obj
 */
function convertJsonToApiFormat(obj: object | undefined): string | undefined {
  if(obj === null || obj === undefined){ return; }
  const urlSearchParams = convertJsonObjectToUrlSearchParams(obj);
  return urlSearchParams.toString();
}

function convertHeadersToJson(headers: Headers){
  const json: {[key: string]: string} = {} ;
  for(let [key, value] of headers.entries()){
    json[key] = value;
  }
  return json;
}

/**
 * Recursively construct a urlSearchParams object that has keys in the form:
 *  parent[prop]
 *  arr[0][id],
 *  etc.
 * @param obj
 * @param searchParams
 * @param parentKey
 */
function convertJsonObjectToUrlSearchParams(obj: object, searchParams = new URLSearchParams(), parentKey?: string): URLSearchParams {
  for (let [key, val] of Object.entries(obj)) {
    const formKey = parentKey ? `${parentKey}[${key}]` : key;
    if (typeof val === "object") {
      convertJsonObjectToUrlSearchParams(val, searchParams, formKey);
    } else {
      searchParams.append(formKey, val);
    }
  }
  return searchParams;
}
